import { useEffect, useRef, useState } from 'react';

/**
 * useScrollWrapper
 *
 * Used when you want to scroll a list, both manually (using native scroll) and programmatically. Currently in use in
 * the ContributorRowBlock and the MenuBlock.
 *
 * This hook uses IntersectionObservers to determine when the first and last item in the list are showing, and from that
 * decide whether or not the implementor of the hook should show scrollForward and scrollBackwards buttons.
 *
 * For more information on intersection observers see below
 * https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 *
 * IntersectionObservers are more performant than attaching listeners to the scroll event and have good support across
 * the board of desired target browsers
 * (https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API#browser_compatibility)
 *
 * @param deps An array of dependencies that will cause a remount of the observers
 * @returns
 */
export const useScrollWrapper = <T extends HTMLElement>(deps: unknown[]) => {
    // This ref is passed back to the component to attach to the scrolling list
    const wrapperRef = useRef<T>(null);

    // Booleans for whether or not to show scroll buttons in the component
    const [showScrollBack, setShowScrollBack] = useState(false);
    const [showScrollForward, setShowScrollForward] = useState(false);

    // Helper function for scrolling smoothly by the width of the wrapper (one chunk at a time)
    const scrollWrapper = (forward = true) => {
        const direction = forward ? 1 : -1;

        return () => {
            wrapperRef.current?.scrollBy({
                left: direction * wrapperRef.current.offsetWidth,
                behavior: 'smooth',
            });
        };
    };

    useEffect(() => {
        // Capture variables for use in clean up
        const wrapper = wrapperRef.current;

        let startIntersectionObserver: IntersectionObserver;
        let endIntersectionObserver: IntersectionObserver;

        let firstChild: Element | null;
        let lastChild: Element | null;

        if (wrapper && wrapper.childElementCount) {
            // Get the first and last child that will be observed
            firstChild = wrapper.firstElementChild;
            lastChild = wrapper.lastElementChild;

            // Define and invoke observe functions for start and end elements
            startIntersectionObserver = new IntersectionObserver(
                ([entry]) => {
                    setShowScrollBack(!entry.isIntersecting);
                },
                {
                    root: wrapper,
                    threshold: 1.0,
                },
            );
            if (firstChild) startIntersectionObserver.observe(firstChild);

            endIntersectionObserver = new IntersectionObserver(
                ([entry]) => setShowScrollForward(!entry.isIntersecting),
                {
                    root: wrapper,
                    threshold: 0.9,
                },
            );
            if (lastChild) endIntersectionObserver.observe(lastChild);
        }

        return () => {
            // Clean up
            if (startIntersectionObserver && firstChild)
                startIntersectionObserver.unobserve(firstChild);
            if (endIntersectionObserver && lastChild)
                endIntersectionObserver.unobserve(lastChild);
        };
        // Being dynamic breaks this eslint rule, so disable
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return {
        wrapperRef,
        showScrollBack,
        showScrollForward,
        scrollWrapper,
    };
};
